import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { Save } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AdminAbout: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    about_title: '',
    about_subtitle: '',
    about_mission: '',
    about_vision: '',
    about_story: '',
    about_team_title: '',
    about_team_description: ''
  });

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const response = await settingsAPI.getAll({ category: 'about' });
      const settings = response.data.data;
      setFormData({
        about_title: settings.about_title || '',
        about_subtitle: settings.about_subtitle || '',
        about_mission: settings.about_mission || '',
        about_vision: settings.about_vision || '',
        about_story: settings.about_story || '',
        about_team_title: settings.about_team_title || '',
        about_team_description: settings.about_team_description || ''
      });
    } catch (error) {
      console.error('Error fetching about content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key: string, value: string) => {
    setSaving(true);
    try {
      await settingsAPI.update({
        key,
        value,
        category: 'about',
        description: `About page - ${key.replace('about_', '')}`
      });
      toast.success('Content updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await Promise.all(
        Object.entries(formData).map(([key, value]) =>
          settingsAPI.update({
            key,
            value,
            category: 'about',
            description: `About page - ${key.replace('about_', '')}`
          })
        )
      );
      toast.success('All content updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Manage About Page</h1>
          <p className="text-gray-600 mt-1">Edit About page content</p>
        </div>

        <div className="max-w-4xl bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Page Title
              </label>
              <input
                type="text"
                value={formData.about_title}
                onChange={(e) => setFormData({ ...formData, about_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="About BSAF"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtitle
              </label>
              <input
                type="text"
                value={formData.about_subtitle}
                onChange={(e) => setFormData({ ...formData, about_subtitle: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Building Africa's Smart Generation"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Our Mission
              </label>
              <textarea
                rows={4}
                value={formData.about_mission}
                onChange={(e) => setFormData({ ...formData, about_mission: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your mission..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Our Vision
              </label>
              <textarea
                rows={4}
                value={formData.about_vision}
                onChange={(e) => setFormData({ ...formData, about_vision: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your vision..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Our Story
              </label>
              <textarea
                rows={6}
                value={formData.about_story}
                onChange={(e) => setFormData({ ...formData, about_story: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Tell your story..."
              />
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Section</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Section Title
                  </label>
                  <input
                    type="text"
                    value={formData.about_team_title}
                    onChange={(e) => setFormData({ ...formData, about_team_title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Meet Our Team"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Description
                  </label>
                  <textarea
                    rows={3}
                    value={formData.about_team_description}
                    onChange={(e) => setFormData({ ...formData, about_team_description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe your team..."
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminAbout;
